import type { LegalCheckFlag } from './types';
import React from 'react';
import { LEGAL_CHECK_CATEGORY_LABELS } from './types';
import './LegalCheckPanel.scss';

interface LegalCheckPanelProps {
  flags: LegalCheckFlag[];
  inFlight: boolean;
  // Current value of the textarea — used to hide flags whose excerpt no longer
  // appears in the text (e.g. the user manually edited it out).
  currentValue: string;
  onDismiss: (flagIndex: number) => void;
}

const LegalCheckPanel: React.FC<LegalCheckPanelProps> = ({ flags, inFlight, currentValue, onDismiss }) => {
  // Preserve original index so dismiss targets the right flag in hook state,
  // even if some flags are filtered out from rendering.
  const visibleFlags = flags
    .map((flag, originalIndex) => ({ flag, originalIndex }))
    .filter(({ flag }) => currentValue.includes(flag.excerpt));

  if (!inFlight && visibleFlags.length === 0) {
    return null;
  }

  return (
    <div
      className="legal-check-panel"
      role="region"
      aria-label="Confidentiality observations"
      data-testid="legal-check-panel"
    >
      {inFlight && (
        <div className="legal-check-panel__loading" aria-live="polite">
          <span className="legal-check-panel__spinner" aria-hidden="true" />
          Checking…
        </div>
      )}

      {visibleFlags.length > 0 && (
        <p className="legal-check-panel__training-note">
          These are the same rules covered in your contributor training. We flag what we think we see — what you do with
          it is up to you.
        </p>
      )}

      {visibleFlags.map(({ flag, originalIndex }) => (
        <div
          key={`${originalIndex}-${flag.excerpt}`}
          className={`legal-check-panel__flag legal-check-panel__flag--${flag.severity}`}
          data-testid="legal-check-panel-flag"
          data-category={flag.category}
        >
          <div className="legal-check-panel__header">
            <span className="legal-check-panel__chip" data-testid="legal-check-panel-category">
              {LEGAL_CHECK_CATEGORY_LABELS[flag.category]}
            </span>
          </div>

          <p className="legal-check-panel__excerpt" data-testid="legal-check-panel-excerpt">
            “{flag.excerpt}”
          </p>

          <p className="legal-check-panel__explanation" data-testid="legal-check-panel-explanation">
            {flag.explanation}
          </p>

          <p className="legal-check-panel__nudge" data-testid="legal-check-panel-nudge">
            Consider revising this before you submit.
          </p>

          <div className="legal-check-panel__actions">
            <button
              type="button"
              className="legal-check-panel__btn legal-check-panel__btn--dismiss"
              data-testid="legal-check-panel-dismiss"
              onClick={() => onDismiss(originalIndex)}
            >
              Got it
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default LegalCheckPanel;
